import { useMutation } from '@tanstack/react-query'
import { useMemo, useState } from 'react'

import { useFlows } from '@/api/flows/flows'
import { useOrder } from '@/api/orders/orders'
import type { Detail } from '@/api/orders/orders-types'
import { useProductionCarts } from '@/api/production-carts/production-carts'
import { productionLogService } from '@/api/production-logs/production-logs-service'
import type { ProductionLogPayload } from '@/api/production-logs/production-logs-types'
import { cn } from '@/lib/utils'
import { AssignCart } from '@/pages/scheduling/components/details/assign-cart'
import { getOrderAssignedCartIds, isCartBlockedByShipDate } from '@/utils/carts'

interface AssignCartProps {
  detail: Detail
  disabled?: boolean
  className?: string
}

export const AssignDetailCart = ({ detail, disabled = false, className }: AssignCartProps) => {
  const currentLog = detail.production_logs?.at(-1)

  const [cart, setCart] = useState(currentLog?.cart_id?.toString())
  // Set on hover/focus of the picker, so the lookup lands before the list opens.
  const [wanted, setWanted] = useState(false)

  const productionLogUpdateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: Partial<ProductionLogPayload> }) =>
      productionLogService.update(id, payload),
    meta: {
      successMessage: `Cart updated successfully`,
      invalidatesQuery: ['items', 'production-carts', 'orders', 'order', 'timeline-orders']
    }
  })

  const { data: flows } = useFlows()
  const { data: carts } = useProductionCarts()

  // A card only carries its own item, so the order's other carts come from a lookup —
  // fired when someone reaches for the picker, not for every card on the board.
  const { data: order } = useOrder(detail.doc_aid, wanted)

  const suggestedCartIds = useMemo(
    () => getOrderAssignedCartIds(detail, order?.details),
    [detail, order?.details]
  )

  const currentFlows =
    flows?.results?.filter((flow) =>
      detail?.standard_times ? Object?.keys(detail?.standard_times)?.includes(flow?.inven) : false
    ) ?? []

  const currentFlowIndex = currentFlows.findIndex((flow) => flow.inven === currentLog?.flow_inven)

  const nextFlow = currentFlowIndex !== -1 ? currentFlows?.[currentFlowIndex + 1] : null

  const currentCarts = useMemo(() => {
    if (!nextFlow) return []
    return (
      carts?.filter(
        (cart) => cart.flow_id === nextFlow?.id && !isCartBlockedByShipDate(cart, detail.ship_date)
      ) ?? []
    )
  }, [nextFlow, carts, detail.ship_date])

  const onCartChange = (cartId: string) => {
    if (currentLog?.id) {
      setCart(cartId)
      productionLogUpdateMutation.mutate({
        id: currentLog?.id,
        payload: {
          cart_id: +cartId
        }
      })
    }
  }

  return (
    <AssignCart
      carts={currentCarts}
      cart={cart}
      disabled={disabled}
      className={cn('h-8', className)}
      maxShipDate={detail.ship_date}
      suggestedCartIds={suggestedCartIds}
      onIntent={() => setWanted(true)}
      onCartChange={onCartChange}
    />
  )
}
